import { MouseEvent, useState } from 'react'
import { CloudUploadOutlined, FullscreenOutlined } from '@ant-design/icons'
import { Alert, Divider, message } from 'antd'
import {
    usefilteredJobRequestLinesByInspectionType,
    isProcessNeitherCertificationNorInspection,
    getDetailString,
    MutationOperations,
} from '@OpsQuality/JobRequestDetails/components/logic'
import { getAngledImage } from '@OpsQuality/logic'
import { AttachmentTypeDataType } from '@OpsQuality/types'
import { PartsLayoutType } from '@OpsQuality/JobRequestDetails/components/types'
import styles from '@OpsQuality/styles.css'
import { attachmentsByProcess } from '@OpsQuality/JobRequestDetails/constants'
import { adminJobRequestQuery_jobRequest_parts_linesWithWorkOrderMapping as JobRequestLineType } from '@OpsQuality/JobRequestDetails/__generated__/adminJobRequestQuery'
import { PROCESS_TYPES_MAP } from '@OpsQuality/constants'

import Box from '~/core/Box'
import Image from '~/core/Image'
import Text from '~/core/Text'
import Button from '~/core/Button'
import TruncatedText from '~/core/TruncatedText'
import FileList from '~/components/FileUploadList'
import getColor from '~/lib/getColor'

import UploadModal from './UploadModal'

export default function PartsTabLayout(props: PartsLayoutType) {
    const {
        partData,
        toggleModalFor3dViewer,
        jobRequestId,
        jobRequestName,
        jobRequestLines,
        isJobCompleted,
    } = props

    const [attachmentUploadModalType, setAttachmentUploadModalType] = useState<{
        jobRequestLinesByProcessTypeOrMaterialType: JobRequestLineType[]
        attachmentTypeData: AttachmentTypeDataType
    } | null>()
    const jobRequestLinesByPart = partData?.linesWithWorkOrderMapping || []
    const { drawingUri, productQty, partProduct, moldTool } =
        jobRequestLinesByPart[0]
    const BLOCKED_POPUPS_DOWNLOAD =
        'We failed to open your download because you have popups blocked.'

    const attachmentTypes = usefilteredJobRequestLinesByInspectionType(
        jobRequestLinesByPart,
    )

    const thumbnail = getAngledImage(jobRequestLinesByPart[0]) || ''

    const mutatingOperation = MutationOperations()

    const onClickCancel = (evt: MouseEvent) => {
        evt.stopPropagation()

        return setAttachmentUploadModalType(null)
    }

    const useOnRemove = async (ids: string[]) => {
        const response = await mutatingOperation.useRemoveJobRequestAttachment(
            ids,
            jobRequestId,
        )

        if (response) {
            message.success('Attachment removed successfully')
        } else {
            message.error('Error occured while removing attachment')
        }
    }

    const useOnSetVisibleToCustomer = async (
        id: string,
        visibleToCustomer: boolean,
    ) => {
        const response = await mutatingOperation.useUpdateVisibleToCustomer(
            id,
            visibleToCustomer,
            jobRequestId,
        )

        if (!response) {
            message.error('Failed to update attachment visibility')
        }
    }

    const openAttachmentUploadModal = (
        event: Event,
        attachmentTypeData: AttachmentTypeDataType,
    ) => {
        event.stopPropagation()
        let jobRequestLinesByProcessTypeOrMaterialType: JobRequestLineType[] =
            []
        let initialJobRequestLine: JobRequestLineType | undefined

        if (
            attachmentTypeData.attachmentProcess?.processType ===
                'certification' ||
            attachmentTypeData.attachmentProcess?.processType === 'inspection'
        ) {
            const jobRequestLineByMaterial =
                partData.linesWithWorkOrderMapping?.find(
                    partJobRequestLine =>
                        partJobRequestLine.processName ===
                        attachmentTypeData.title,
                )

            jobRequestLinesByProcessTypeOrMaterialType = jobRequestLines.filter(
                jobRequestLine => {
                    if (
                        jobRequestLine.processName === attachmentTypeData.title
                    ) {
                        if (
                            attachmentTypeData.attachmentProcess
                                .attachmentType === 'material_certification' &&
                            jobRequestLineByMaterial?.materialName !==
                                jobRequestLine.materialName
                        ) {
                            return
                        }
                        if (jobRequestLine.partProduct?.id === partData.id) {
                            initialJobRequestLine = jobRequestLine
                            return
                        }
                        return jobRequestLine
                    }
                },
            )
            if (initialJobRequestLine) {
                jobRequestLinesByProcessTypeOrMaterialType.unshift(
                    initialJobRequestLine,
                )
            }
            if (
                attachmentTypeData.attachmentProcess.attachmentType ===
                    'advanced_inspection' &&
                initialJobRequestLine
            ) {
                jobRequestLinesByProcessTypeOrMaterialType = [
                    initialJobRequestLine,
                ]
            }

            return setAttachmentUploadModalType({
                attachmentTypeData,
                jobRequestLinesByProcessTypeOrMaterialType,
            })
        }

        return setAttachmentUploadModalType({
            attachmentTypeData,
            jobRequestLinesByProcessTypeOrMaterialType:
                jobRequestLinesByPart.filter(
                    isProcessNeitherCertificationNorInspection,
                ),
        })
    }

    const handle2dDrawingOnClick = () => {
        if (!drawingUri) {
            message.error('Failed to download drawing')
            return
        }
        try {
            window.open(drawingUri, '_blank', 'noopener')
        } catch {
            message.error(BLOCKED_POPUPS_DOWNLOAD)
        }
    }

    return (
        <Box
            direction="col"
            height="100%"
            className={styles.cardLayoutPart}
            maxWidth="406px"
            padding="16px 0"
        >
            <Box justifyContent="between" padding="16px 16px">
                <Box className={styles.partName}>
                    <TruncatedText maxLength={25}>
                        {partProduct?.partFileName || moldTool?.name || ''}
                    </TruncatedText>
                </Box>
                <Box className={styles.partQuantity}>Qty {productQty}</Box>
            </Box>
            <Box
                direction="col"
                margin="16px"
                border={`1px solid ${getColor('blackAlpha15')}`}
                onClick={() =>
                    toggleModalFor3dViewer(
                        true,
                        partProduct?.partProductRevision?.basicRevision
                            ?.sceneBundle,
                    )
                }
                style={{ cursor: 'pointer' }}
            >
                <Box alignItems="center" justifyContent="center">
                    <Image
                        width="140px"
                        height="140px"
                        alt=""
                        src={thumbnail}
                    />
                </Box>
                <Box
                    justifyContent="end"
                    padding="0 16px 8px"
                    className={moldTool ? styles.disabledLink : ''}
                >
                    <FullscreenOutlined style={{ margin: '5px 5px 0 0' }} />
                    <Text fontSize="16px" fontWeight={400}>
                        View in 3D
                    </Text>
                </Box>
            </Box>
            <Box padding="0 16px">
                <Button
                    name="view2DBtn"
                    placement="inventory-detail-card"
                    disabled={!drawingUri || !!moldTool}
                    style={{
                        width: '100%',
                        marginBottom: '8px',
                        fontWeight: 400,
                    }}
                    onClick={handle2dDrawingOnClick}
                >
                    View 2D
                </Button>
            </Box>
            {jobRequestLinesByPart.map(jobRequestLine => (
                <Box key={jobRequestLine.id} padding="8px 16px 3px">
                    <Box direction="col" width="240px">
                        <Box
                            className={
                                (styles.clearFiltersSpan,
                                styles.partJobRequestName)
                            }
                        >
                            {jobRequestLine.name || 'other'}
                        </Box>
                        <Box
                            className={
                                (styles.clearFiltersSpan,
                                styles.partProcessName)
                            }
                            style={{ fontWeight: 400 }}
                        >
                            {jobRequestLine.processName}
                            {jobRequestLine.processType ===
                                PROCESS_TYPES_MAP.CNC ||
                            jobRequestLine.processType === PROCESS_TYPES_MAP.TDP
                                ? jobRequestLine.materialName
                                : null}
                        </Box>
                        <Box
                            style={{ fontWeight: 300 }}
                            fontSize="14px"
                            color={getColor('blackAlpha45')}
                        >
                            {getDetailString(jobRequestLine)}
                        </Box>
                    </Box>
                </Box>
            ))}
            <Divider style={{ margin: '5px 0 0 0' }} />
            {Object.keys(attachmentTypes)
                .sort(item => {
                    return item === 'fictiv5' || item === 'inspection_Photos'
                        ? -1
                        : 1
                })
                .map(item => {
                    const currentTypeAttachments = attachmentTypes[item]
                    const attachmentTypeData = attachmentsByProcess[item]
                    const { title } = attachmentTypeData

                    return (
                        <Box
                            direction="col"
                            padding="0 16px 8px"
                            className={styles.uploadCard}
                            key={item}
                        >
                            <Box justifyContent="between" padding="16px 0 16px">
                                <Text className={styles.partProcessName}>
                                    {title}
                                </Text>
                                <Box>
                                    <Button
                                        name="upload-documents"
                                        placement="inventory-detail-card"
                                        style={{
                                            fontWeight: 400,
                                        }}
                                        onClick={(event: Event) => {
                                            openAttachmentUploadModal(
                                                event,
                                                attachmentTypeData,
                                            )
                                        }}
                                    >
                                        <CloudUploadOutlined /> Upload
                                    </Button>
                                </Box>
                            </Box>
                            <Box width="100%">
                                {!currentTypeAttachments?.length && (
                                    <Alert
                                        message="No files uploaded"
                                        type="error"
                                        style={{
                                            width: '100%',
                                            fontWeight: 400,
                                        }}
                                    />
                                )}
                            </Box>
                            <Box
                                justifyContent="between"
                                className={styles.uploadUiWrapper}
                            >
                                <FileList
                                    files={currentTypeAttachments}
                                    modalControls={{
                                        backFwd: true,
                                        rotate: true,
                                        zoom: true,
                                        canBeDeleted: true,
                                        newWindow: true,
                                    }}
                                    modalCss={styles.docViewerModal}
                                    modalTitle
                                    onRemove={useOnRemove}
                                    typeOfModule={'inventoryQc'}
                                    showIcons
                                    attachmentTitle={title}
                                    onSetVisibleToCustomer={
                                        useOnSetVisibleToCustomer
                                    }
                                    showVisibilityIcon
                                />
                            </Box>
                        </Box>
                    )
                })}
            {attachmentUploadModalType &&
                Object.keys(attachmentUploadModalType).length > 0 && (
                    <UploadModal
                        jobRequestId={jobRequestId}
                        jobRequestName={jobRequestName}
                        onCancel={onClickCancel}
                        attachmentTypeDetails={attachmentUploadModalType}
                        isJobCompleted={isJobCompleted}
                    />
                )}
        </Box>
    )
}
